import { useBaseUser } from '@hooks/queries/useBaseUser';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useBoundStore } from '@state/store';
import { useQuery } from '@tanstack/react-query';
import { getPropertiesPermissions } from '@utils/properties.utils';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { BaseApi } from '../../core/api';
import { setSelectedProperty } from '../../redux/properties/thunks';

import type { Property } from '../../core/models/properties';

export function useProperty() {
  const dispatch = useDispatch();
  const { setSelectedField } = useBoundStore().regions;
  const { organization } = useOrganization();
  const { user } = useBaseUser();
  const { propertyId } = useParams();
  const ldClient = useLDClient();

  const fetchProperty = async ({ signal }: { signal?: AbortSignal }) => {
    const controller = new AbortController();
    signal?.addEventListener('abort', () =>
      controller.abort('Query was cancelled by TanStack Query')
    );

    const { data: property } = await BaseApi.get<Property>(`/properties/${propertyId}`, {
      signal: controller.signal,
    });

    const [verifiedProperty] = await getPropertiesPermissions([property]);

    return {
      ...verifiedProperty,
      workspace_use_type: organization?.workspace_use_type,
      country_code: organization?.country,
    } as Property;
  };

  const { data: property, ...queryRest } = useQuery({
    queryKey: ['property', { propertyId }],
    queryFn: fetchProperty,
    enabled: Boolean(propertyId && organization && user),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // React to the loaded property changing instead of using onSuccess: with
  // staleTime Infinity, switching back to a cached property never refetches
  useEffect(() => {
    if (!property || property.id === globalThis.propertyId) return;
    globalThis.propertyId = property.id;

    ldClient?.identify({
      kind: 'multi',
      property: {
        key: property.id,
        name: property.name,
      },
      organization: {
        key: organization?.id,
        name: organization?.name,
      },
      user: {
        key: user?.id,
        name: user?.name,
        default_workspace: user?.default_workspace_id,
      },
    });

    setSelectedField(null);
    dispatch(setSelectedProperty());
  }, [property, ldClient, organization, user, setSelectedField, dispatch]);

  return { property, ...queryRest };
}
